import { useMemo } from 'react';
import { useAppSelector } from '../../../../../../redux/hooks';
import {
  normalizeOrgCatalog,
  type MemberAttributeGroupCatalog
} from '../../../../utils/memberAttributes';

// Flattens the org-level `memberAttributes` (shape returned by getOrgName:
// `[{ name, values: [{ _id, value }] }]`) into a single flat list of
// audience options keyed by attribute-value id. Used by the audience
// picker (drawer + inline editor) so the FE sends ids to the BE — which
// is what `group.memberAttributes` stores — while still showing readable
// labels in the UI.

export interface AudienceOption {
  id: string; // the value's _id; this is what gets stored on the group
  label: string; // value.value — what the user sees
  attributeName: string; // owning attribute (e.g. "Department") — for grouping if needed
}

export const useOrgAudienceOptions = (): {
  options: AudienceOption[];
  labelById: Map<string, string>;
  /** The same catalog with list boundaries preserved — the per-list Any/All
   *  audience UI needs the owning list's `_id`/`name`, which the flattened
   *  `options` array throws away. Reuses normalizeOrgCatalog so the org-level
   *  hidden-attribute filtering stays consistent with the rest of the app. */
  catalog: MemberAttributeGroupCatalog[];
} => {
  const memberAttributes = useAppSelector((state) => {
    const data = state.orgDetails.data as
      | { memberAttributes?: Array<{ name?: string; values?: Array<{ _id?: string; value?: string }> }> }
      | undefined;
    return data?.memberAttributes ?? [];
  });

  return useMemo(() => {
    const catalog = normalizeOrgCatalog(memberAttributes);
    const options: AudienceOption[] = [];
    const labelById = new Map<string, string>();
    for (const list of catalog) {
      for (const v of list.values) {
        if (!v._id || !v.value) continue;
        const id = String(v._id);
        options.push({ id, label: v.value, attributeName: list.name });
        labelById.set(id, v.value);
      }
    }
    return { options, labelById, catalog };
  }, [memberAttributes]);
};

// Convert a list of audience value-ids back to their human labels.
// Unknown ids are dropped (defensive — if an attribute value was deleted
// at org level after a program saved it, we don't want to leak the id).
export const resolveAudienceLabels = (
  ids: string[] | null | undefined,
  labelById: Map<string, string>
): string[] => {
  if (!Array.isArray(ids)) return [];
  const out: string[] = [];
  for (const id of ids) {
    const label = labelById.get(String(id));
    if (label) out.push(label);
  }
  return out;
};
